// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Fragments
import { ReconciliationFragment } from "../fragments/reconciliation";

// Types
import type { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import type {
	IPerformReconciliationResponse,
	IUpdateReconciliationInput,
	IUpdateReconciliationResponse,
	TReconciliationType,
} from "@/DataPages/components/Inventory/types/reconciliation";
import type { IPresignedUrl } from "@DataPages/types/common/s3";

export async function performReconciliation({
	locationId,
	commissionCycleId,
	payPeriodId,
	types,
}: {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	types: TReconciliationType[];
}) {
	const query = gql`
		mutation PerformReconciliation($input: PerformReconciliationInput) {
			performReconciliation(input: $input)
		}
	`;

	const variables = {
		input: { locationId, commissionCycleId, payPeriodId, types },
	};

	try {
		const response: GraphqlQueryResponse<"performReconciliation", IPerformReconciliationResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.performReconciliation;
	} catch (err) {
		errorLogger(err as Error, { function: "updateReconciliationData", message: "Failed to get reconciliation data." });
		return null;
	}
}

export async function exportReconciliation(locationId: string, commissionCycleId: string, payPeriodId: string, vehicleTypes: string[]) {
	const query = gql`
		mutation exportReconciliation($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $vehicleTypes: [String]!) {
			exportReconciliation(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				vehicleTypes: $vehicleTypes
			) {
				fileName
				presignedUrl
				error {
					message
					status
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		vehicleTypes,
	};

	try {
		const response: GraphqlQueryResponse<"exportReconciliation", IPresignedUrl[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.exportReconciliation;
	} catch (err) {
		errorLogger(err as Error, { function: "exportReconciliation", message: "Failed to export reconciliation." });
		return null;
	}
}

export async function updateReconciliation(
	locationId: string,
	commissionCycleId: string,
	payPeriodId: string,
	input: IUpdateReconciliationInput
) {
	const query = gql`
		mutation updateReconciliation(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$input: UpdateReconciliationInput!
		) {
			updateReconciliation(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId, input: $input) {
				locationId
				commissionCycleId
				payPeriodId
				reconciliation {
					...Reconciliation
				}
			}
		}
		${ReconciliationFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		input,
	};

	try {
		const response: GraphqlQueryResponse<"updateReconciliation", IUpdateReconciliationResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateReconciliation;
	} catch (err) {
		errorLogger(err as Error, { function: "updateReconciliation", message: "Failed to update reconciliation." });
		return null;
	}
}
